import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { HyperText } from '../components/HyperText';
import { WordFadeIn } from '../components/ui/WordFadeIn';
import { motion } from 'framer-motion';
import { FadeIn } from '../components/animations/FadeIn';
import { SlideIn } from '../components/animations/SlideIn';
import { ScrollReveal } from '../components/animations/ScrollReveal';
import { ShieldCheck, Target, Heart, Clock, School, Building, Users, AlertTriangle, ArrowRight } from 'lucide-react';
import cn from 'classnames';
import { ImageFadeIn } from '../components/animations/ImageFadeIn';
import { useDonateByMail } from '../contexts/DonateByMailContext';
// Lazy load the WhatWeDoFeature component
const WhatWeDoFeature = lazy(() => import('../components/WhatWeDoFeature'));
const HomePage = () => {
  const {
    openDonateByMail
  } = useDonateByMail();
  return <div className="w-full" data-id="element-497" data-mp="29bg66e8c7ooq">
      {/* Hero Section */}
      <div className="w-full relative min-h-screen" data-id="element-498" data-mp="298tymelg596i">
        <div className="absolute inset-0 w-full h-full overflow-hidden" data-id="element-499" data-mp="2apoe67lsmfim">
          <div className="absolute inset-0 bg-israel-blue/30 mix-blend-overlay z-10" data-id="element-500" data-mp="2eq8axa2gfh48"></div>
          <div className="absolute inset-0 bg-blue-900/10 z-10" data-id="element-501" data-mp="2jh6secz6il92"></div>
          <iframe src="https://player.vimeo.com/video/1102199157?badge=0&autopause=0&player_id=0&app_id=58479&background=1&autoplay=1&loop=1&muted=1" className="absolute w-[400%] sm:w-[200%] md:w-full h-full -left-[150%] sm:-left-[50%] md:left-0 top-0 object-cover" style={{
          pointerEvents: 'none',
          transform: 'scale(1.5)',
          transformOrigin: 'center center'
        }} loading="lazy" fetchpriority="high" importance="high" title="israel site final" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" frameBorder="0" data-id="element-502" data-mp="2jhyorq0lon0e"></iframe>
        </div>
        <div className="relative z-20 flex flex-col justify-end h-screen pb-16 sm:pb-24 px-4 sm:px-6" data-id="element-503" data-mp="2riwe3xe35zql">
          <div className="max-w-7xl mx-auto w-full" data-id="element-504" data-mp="2mjt6c56q1fex">
            <div className="mx-auto text-center" data-id="element-505" data-mp="31dy1anoswrqj">
              <FadeIn delay={0.2} data-id="element-506" data-mp="31cmx4mrg1hyw">
                <div className="flex justify-center mb-6 sm:mb-8" data-id="element-507" data-mp="2wfmrue2f4j8u">
                  <ImageFadeIn src="https://cdn.magicpatterns.com/uploads/eTred5kVLFu4Xp2DvYu8SK/new_ictf_logo_real.png" alt="Israeli Civilian Training Foundation Logo" className="h-20 sm:h-24 w-auto" data-id="element-508" data-mp="28qbn6lhy64pf" />
                </div>
              </FadeIn>
              <SlideIn delay={0.4} data-id="element-509" data-mp="2dov9in0gdiib">
                <div className="mb-3 sm:mb-4" data-id="element-510" data-mp="2dnu00mz3542p">
                  <HyperText text="FORTIFYING ISRAEL" className="text-white text-2xl sm:text-3xl md:text-4xl" duration={1200} data-id="element-511" data-mp="1pzkhbtx64wfs" />
                  <HyperText text="ONE FAMILY AT A TIME" className="text-white text-2xl sm:text-3xl md:text-4xl" duration={1200} data-id="element-512" data-mp="iymdl1tilt8" />
                </div>
              </SlideIn>
              <FadeIn delay={0.6} data-id="element-513" data-mp="3mrq7mcuhf1qi">
                <WordFadeIn words="The Israeli Civilian Training Fund (ICTF) equips Israeli families and communities with the practical training needed to survive, defend, and protect what matters most." className="text-base sm:text-lg md:text-xl text-gray-100 mb-8 leading-relaxed font-light max-w-2xl mx-auto px-2 sm:px-0" delay={0.8} variants={{
                hidden: {
                  opacity: 0,
                  y: 10
                },
                visible: (i: number) => ({
                  y: 0,
                  opacity: 1,
                  transition: {
                    delay: 0.4 + i * 0.02,
                    duration: 0.2
                  }
                })
              }} data-id="element-514" data-mp="3roqdonrc04hm" />
              </FadeIn>
              <FadeIn delay={1} data-id="element-515" data-mp="3aenvguv4nq8h">
                <motion.div whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} transition={{
                duration: 0.2
              }} data-id="element-516" data-mp="35h4gfsqlv9fh">
                  <Link to="/support" className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-israel-blue text-white font-medium rounded-lg hover:bg-israel-blue-light transition-all duration-300" data-id="element-517" data-mp="3t6yqk2jjijw1">
                    Support the Mission
                  </Link>
                </motion.div>
              </FadeIn>
              <FadeIn delay={1.2} data-id="element-518" data-mp="2cqnj22ch57mj">
                <p className="text-xl sm:text-2xl md:text-3xl text-white font-bold italic mt-4" data-id="element-519" data-mp="28r555qj2lwt7">
                  "Protecting a nation, Preserving a generation"
                </p>
              </FadeIn>
            </div>
          </div>
        </div>
      </div>
      {/* Urgent Need Section */}
      <ScrollReveal animation="fade" data-id="element-520" data-mp="x9silv5opc02">
        <div className="bg-white py-16 sm:py-24" data-id="element-521" data-mp="x8psvqk3lflq">
          <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-522" data-mp="121akqmjg1imm">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-523" data-mp="122u2yionu3r1">
              <SlideIn direction="left" data-id="element-524" data-mp="16zc5rd0fjs0q">
                <div data-id="element-525" data-mp="1byoopmo6key1">
                  <h2 className="text-3xl sm:text-4xl font-heading text-gray-900 mb-6" data-id="element-526" data-mp="1by5gixoc14x7">
                    When Minutes Matter, Training Saves Lives
                  </h2>
                  <div className="space-y-4 text-gray-700 mb-8" data-id="element-527" data-mp="1kzmxei9kdamy">
                    <p className={cn('text-lg leading-relaxed')} data-id="element-528" data-mp="1kytu5r9td39k">
                      In October 2023, Israel faced its deadliest attack in
                      decades. Communities across Israel—particularly those near
                      borders—found themselves isolated and vulnerable as
                      security forces were stretched beyond capacity.
                    </p>
                    <p className={cn('text-lg leading-relaxed')} data-id="element-529" data-mp="oyzn0892d4f2">
                      Today, Israel continues to face unprecedented security
                      challenges on multiple fronts. With IDF forces deployed
                      and reserves repeatedly mobilized, the threat to Israeli
                      civilians has reached a critical level. We are equipping
                      everyday Israelis with the training, tools, and mindset
                      needed to protect their families, support their
                      communities, and stand resilient in times of crisis. When
                      seconds matter and help is minutes away, trained civilians
                      become the first line of defense.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4" data-id="element-530" data-mp="1qobpf7ei6a9q">
                    <motion.div whileHover={{
                    scale: 1.05
                  }} whileTap={{
                    scale: 0.95
                  }} data-id="element-531" data-mp="2a8tuc028hdfc">
                      <Link to="/about" className="inline-flex items-center px-6 py-3 bg-israel-blue text-white font-medium rounded-lg hover:bg-israel-blue-light transition-all duration-300" data-id="element-532" data-mp="209uw7bf3h06l">
                        Learn More
                        <motion.span animate={{
                        x: [0, 5, 0]
                      }} transition={{
                        duration: 1,
                        repeat: Infinity
                      }} data-id="element-533" data-mp="1wacawejd2n38">
                          <ArrowRight className="ml-2 w-4 h-4" data-id="element-534" data-mp="18lkpc60a12zf" />
                        </motion.span>
                      </Link>
                    </motion.div>
                    <Link to="/training-center" className="inline-flex items-center px-6 py-3 border border-israel-blue text-israel-blue font-medium rounded-lg hover:bg-blue-50 transition-colors" data-id="element-535" data-mp="1szstsuhjwjww">
                      Explore Our Solution
                    </Link>
                  </div>
                </div>
              </SlideIn>
              <SlideIn direction="right" data-id="element-536" data-mp="1a300iy8dvj2k">
                <div className="relative" data-id="element-537" data-mp="15aqh811rxh99">
                  <motion.div animate={{
                  scale: [1, 1.02, 1],
                  rotate: [0, 1, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="absolute -top-6 -left-6 w-24 h-24 bg-blue-100 rounded-full opacity-50" data-id="element-538" data-mp="159yiihcvr3er" />
                  <div className="relative z-10 rounded-xl overflow-hidden shadow-xl" data-id="element-539" data-mp="2xzhm1dsdqdz5">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9cpDLWMPsawsEL33rME46D/F230719AM20.jpg" alt="Armed personnel in training scenario" className="w-full h-auto" data-id="element-540" data-mp="320t4mzcu5yxu" />
                  </div>
                  <motion.div animate={{
                  scale: [1, 1.05, 1],
                  rotate: [0, -1, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 0.5
                }} className="absolute -bottom-6 -right-6 w-32 h-32 bg-israel-blue/10 rounded-full" data-id="element-541" data-mp="2eb8kwe8uyxni" />
                </div>
              </SlideIn>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Key Stats Section */}
      <ScrollReveal animation="fade" data-id="element-542" data-mp="2bpmf9ul8cv43">
        <div className="bg-gray-50 py-16 sm:py-24" data-id="element-543" data-mp="2bsqr9e4xsv5b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-544" data-mp="2goxxz955qaob">
            <div className="text-center mb-12" data-id="element-545" data-mp="2grk6pc3saw84">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-546" data-mp="2lpvgjmu2x6qc">
                The Challenge & Our Goals
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-547" data-mp="2qlu6vfyywyte">
                Israel faces unprecedented security threats that demand a new
                approach to civilian preparedness
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-548" data-mp="22xsw00ebh6at">
              <ScrollReveal animation="slide-up" delay={0.1} data-id="element-549" data-mp="22whrtzgylwj6">
                <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 transform transition-all hover:scale-105 hover:shadow-lg" data-id="element-550" data-mp="27puew55zpv9t">
                  <div className="flex items-start" data-id="element-551" data-mp="1f8rsmrkcclce">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-552" data-mp="1f76y31y112ej">
                      <AlertTriangle className="w-6 h-6 text-red-500" data-id="element-553" data-mp="3sgx7ngtwk3c5" />
                    </div>
                    <div data-id="element-554" data-mp="3ofbnrbiyaosg">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-555" data-mp="3ji37jghd4utu">
                        Urgent Need
                      </h3>
                      <p className="text-gray-600" data-id="element-556" data-mp="3eigvvbh6nmn5">
                        Israeli civilians, particularly in border communities,
                        face ongoing security threats with limited access to
                        training
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.2} data-id="element-557" data-mp="2vtai82v8xl87">
                <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 transform transition-all hover:scale-105 hover:shadow-lg" data-id="element-558" data-mp="2vqo23euahxed">
                  <div className="flex items-start" data-id="element-559" data-mp="2lub7meqx7fik">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-560" data-mp="2lwxesd9wft09">
                      <Users className="w-6 h-6 text-israel-blue" data-id="element-561" data-mp="2h5s7onv05wdv" />
                    </div>
                    <div data-id="element-562" data-mp="236k6xqj6yg7k">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-563" data-mp="237c59946as1j">
                        Training Goal
                      </h3>
                      <p className="text-gray-600" data-id="element-564" data-mp="279yqusa93gqa">
                        Our mission is to train 50,000 civilians annually
                        through free, comprehensive security programs
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.3} data-id="element-565" data-mp="2q6qflh8kao1q">
                <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 transform transition-all hover:scale-105 hover:shadow-lg" data-id="element-566" data-mp="2q4x8k0u1e1t7">
                  <div className="flex items-start" data-id="element-567" data-mp="3inko70q9komw">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-568" data-mp="3ioc9niq5d5zg">
                      <Building className="w-6 h-6 text-green-600" data-id="element-569" data-mp="128z30wg4rl80" />
                    </div>
                    <div data-id="element-570" data-mp="1274r27mu73g2">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-571" data-mp="1gyeru6w3pgl2">
                        Project Cost
                      </h3>
                      <p className="text-gray-600" data-id="element-572" data-mp="1bzkbrcpiq64n">
                        $14.1M needed to build Israel's first dedicated civilian
                        training center
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
            <div className="mt-12 text-center" data-id="element-573" data-mp="y5ax0i75cxov">
              <Link to="/roadmap" className="inline-flex items-center text-israel-blue hover:underline font-medium" data-id="element-574" data-mp="t818d4rl0q23">
                View our implementation timeline
                <ArrowRight className="ml-2 w-4 h-4" data-id="element-575" data-mp="24mo9cwma7i6e" />
              </Link>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Our Approach Section */}
      <ScrollReveal animation="fade" data-id="element-576" data-mp="j4xavj8u16sc">
        <div className="bg-white py-16 sm:py-24" data-id="element-577" data-mp="j5yw2gjkb9ns">
          <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-578" data-mp="n7ai6c389cr8">
            <div className="text-center mb-16" data-id="element-579" data-mp="n951xn2riytq">
              <div className="inline-flex items-center justify-center mb-4" data-id="element-580" data-mp="s6dm1t7h1cxm">
                <Target className="w-8 h-8 text-israel-blue mr-3" data-id="element-581" data-mp="x5860yh6d7jb" />
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900" data-id="element-582" data-mp="x4ye35ckkqaz">
                  Our Approach
                </h2>
              </div>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-583" data-mp="16uhxqd83wo6w">
                Comprehensive training designed to equip Israeli civilians with
                life-saving skills
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-584" data-mp="1fwr7v1zgqfjr">
              <ScrollReveal animation="slide-up" delay={0.1} data-id="element-585" data-mp="1kuj4uaeysrut">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300" data-id="element-586" data-mp="1ksolh0jcd7su">
                  <div className="flex justify-center mb-6" data-id="element-587" data-mp="1pz3hh6rkmlk4">
                    <div className="w-16 h-16 rounded-full bg-israel-blue/10 flex items-center justify-center" data-id="element-588" data-mp="1q1pnv32q6v0r">
                      <ShieldCheck className="w-8 h-8 text-israel-blue" data-id="element-589" data-mp="1l2d0mh7xzlxn" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center" data-id="element-590" data-mp="2dpqrbxl1rl9b">
                    Prepare
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-591" data-mp="28qnx786luzg4">
                    Uniform, legally grounded training in self-defense,
                    emergency medicine, and crisis response.
                  </p>
                  <ul className="space-y-2" data-id="element-592" data-mp="1zxatdb706df9">
                    <li className="flex items-start" data-id="element-593" data-mp="2wfgmwmwmwb2a">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-594" data-mp="2wh00u6utia0w">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-595" data-mp="prg66mouv6e8"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-596" data-mp="uq9n6p3cpfj5">
                        Situational awareness training
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-597" data-mp="fzk6x8b19a8f">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-598" data-mp="kzogsfhfr6po">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-599" data-mp="6znhv5kqe9cu"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-600" data-mp="bxoxfgiy7qjk">
                        Weapon proficiency & safety
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-601" data-mp="22nc6vrsn8m9">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-602" data-mp="1rh1uax8b3x8s">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-603" data-mp="1wetsua3gihly"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-604" data-mp="3gygw072m8xdh">
                        Emergency response protocols
                      </span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-605" data-mp="3qoiln3amanui">
                    <Link to="/programs" className="text-israel-blue hover:underline text-sm font-medium flex items-center" data-id="element-606" data-mp="3lwid4mghemmb">
                      Learn more
                      <ArrowRight className="ml-1 w-3 h-3" data-id="element-607" data-mp="3vm1xxxnr8qjt" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.2} data-id="element-608" data-mp="8idnjz3oj4o3">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300" data-id="element-609" data-mp="8ix53epf3r1l">
                  <div className="flex justify-center mb-6" data-id="element-610" data-mp="2a9qqiim372hi">
                    <div className="w-16 h-16 rounded-full bg-israel-blue/10 flex items-center justify-center" data-id="element-611" data-mp="2abbka60kuheb">
                      <School className="w-8 h-8 text-israel-blue" data-id="element-612" data-mp="1oe8l1m2hgk0s" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center" data-id="element-613" data-mp="1y64kp1k7wi74">
                    Equip
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-614" data-mp="23466vvcxwxmt">
                    State-of-the-art facilities providing hands-on training in
                    realistic environments.
                  </p>
                  <ul className="space-y-2" data-id="element-615" data-mp="1ahlf4tsjyjiq">
                    <li className="flex items-start" data-id="element-616" data-mp="1aidbi6tz4la2">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-617" data-mp="1feliuy1xja6o">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-618" data-mp="1kcn3hnf07hk9"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-619" data-mp="2qx4slnlo6642">
                        Live-fire ranges with moving targets
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-620" data-mp="k601g89u19gw">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-621" data-mp="f6x0aywz3ne7">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-622" data-mp="aff6qvxa4p39"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-623" data-mp="5hdeplhvlffn">
                        3 MILO Range simulators for scenario training
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-624" data-mp="3sljw9bq5izy2">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-625" data-mp="3nn06f08ft40f">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-626" data-mp="3ip71sbms8i29"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-627" data-mp="1lukam395ctog">
                        Trauma manikins for medical training
                      </span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-628" data-mp="28pk21yp51h1k">
                    <Link to="/training-center" className="text-israel-blue hover:underline text-sm font-medium flex items-center" data-id="element-629" data-mp="23rig960bv3me">
                      Explore our facilities
                      <ArrowRight className="ml-1 w-3 h-3" data-id="element-630" data-mp="1u1p6majqwyks" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.3} data-id="element-631" data-mp="1l111648f46ji">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300" data-id="element-632" data-mp="1l3n6dx7u69yk">
                  <div className="flex justify-center mb-6" data-id="element-633" data-mp="1b3wgk8u80j4t">
                    <div className="w-16 h-16 rounded-full bg-israel-blue/10 flex items-center justify-center" data-id="element-634" data-mp="1b5h5nixrn9va">
                      <Heart className="w-8 h-8 text-israel-blue" data-id="element-635" data-mp="35hdt70t37vl2" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center" data-id="element-636" data-mp="3dyxz5ngizzmv">
                    Protect
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-637" data-mp="3hxx0ky31te9k">
                    Building community resilience through comprehensive training
                    and preparation.
                  </p>
                  <ul className="space-y-2" data-id="element-638" data-mp="3rp1gq14gn55z">
                    <li className="flex items-start" data-id="element-639" data-mp="2uzk04r3xvypi">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-640" data-mp="2v0v52u94fci7">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-641" data-mp="33zyo5y8afrwp"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-642" data-mp="38ys3lw74xszi">
                        Home defense strategies
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-643" data-mp="j9hrreato8wq">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-644" data-mp="o81ehgx8poq5">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-645" data-mp="3uoi2g582uhbw"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-646" data-mp="3pqq874jymj4j">
                        Life-saving medical intervention
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-647" data-mp="3l36qih8v7j1">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-648" data-mp="3bzla46w8pywb">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-649" data-mp="371jphhj61riq"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-650" data-mp="3lq6g4fe8u5p9">
                        Community coordination techniques
                      </span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-651" data-mp="2t7sq31z5fo0q">
                    <Link to="/about" className="text-israel-blue hover:underline text-sm font-medium flex items-center" data-id="element-652" data-mp="2oairowts820r">
                      About our mission
                      <ArrowRight className="ml-1 w-3 h-3" data-id="element-653" data-mp="18jgka6x3a9fe" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Timeline Section */}
      {/* Call to Action */}
      <ScrollReveal animation="fade" data-id="element-654" data-mp="1mi6gzlfzy738">
        <div className="bg-israel-blue py-16 sm:py-24" data-id="element-655" data-mp="1mj93lh6adndc">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-white" data-id="element-656" data-mp="1rhsvrzbh3ve5">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-id="element-657" data-mp="1di1hdxh1oczm">
              Join Our Mission to Protect Israeli Communities
            </h2>
            <p className="text-xl opacity-90 mb-10 max-w-3xl mx-auto font-light" data-id="element-658" data-mp="1hhs8616kmt45">
              Every contribution helps us build a safer, more resilient Israel
              through civilian training and preparedness.
            </p>
            <div className="flex flex-wrap justify-center gap-6" data-id="element-659" data-mp="mbcfhj493dq1">
              <a href="https://donate.stripe.com/7sY5kw7P1eO24x90y314401" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-white text-israel-blue font-semibold rounded-lg hover:bg-gray-100 transition-colors text-lg shadow-lg" data-id="element-660" data-mp="mcxdjiprxesn">
                Donate Online
              </a>
              <button type="button" onClick={openDonateByMail} className="inline-block px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors text-lg" data-id="element-661" data-mp="158vssyxqrii9">
                Donate by Mail
              </button>
              <Link to="/support" className="inline-block px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors text-lg" data-id="element-662" data-mp="rci9dk9nd715">
                Support Our Mission
              </Link>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>;
};
export default HomePage;